// Central date formatter — "2nd Mar" (current year) / "2nd Mar 2025" (other years).
// Used by every UI surface that displays dates so the studio looks consistent.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// The whole studio operates on IST office hours. Every check-in/check-out and
// other wall-clock timestamp must always be SHOWN in IST — regardless of the
// viewer's device/browser timezone setting (a mis-set laptop clock should
// never make attendance times look wrong).
const IST_TIMEZONE = "Asia/Kolkata";

function ordinal(n) {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

// Accepts: Date | ISO string | "YYYY-MM-DD" | "YYYY-MM-DD HH:MM:SS" | epoch ms.
// Returns a Date or null when the input cannot be parsed.
function toDate(input) {
  if (input === null || input === undefined || input === "") return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === "number") {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof input !== "string") return null;
  let s = input.trim();
  if (!s) return null;
  // "YYYY-MM-DD" — parse as local date to avoid timezone day-shift.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  // "YYYY-MM-DD HH:MM[:SS]" — replace space with T so it parses as ISO.
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(s)) {
    s = s.replace(" ", "T");
    // A string with no "Z"/±HH:MM offset is a naive wall-clock timestamp
    // (see backend `now_ist_naive_iso()`) and is ALWAYS IST. Pin the offset
    // explicitly so it resolves to the correct absolute instant no matter
    // what timezone the viewer's device happens to be set to — instead of
    // letting the JS engine silently assume the device's local timezone.
    if (!/(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
      s = `${s}+05:30`;
    }
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

// Pull {day, month(1-12), year, hh, mm} out of a Date, always rendered in
// IST via Intl — independent of getHours()/getMonth() etc, which read the
// device's LOCAL timezone and are exactly what caused times to drift for
// anyone whose laptop/browser isn't set to Asia/Kolkata.
function istFields(d) {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TIMEZONE,
    day: "numeric", month: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = fmt.formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value || "0";
  return {
    day: Number(get("day")),
    month: Number(get("month")),
    year: Number(get("year")),
    hh: get("hour").padStart(2, "0"),
    mm: get("minute").padStart(2, "0"),
  };
}

// "2nd Mar" or "2nd Mar 2025" (year shown only when ≠ current year).
export function formatDate(input, { fallback = "—" } = {}) {
  const d = toDate(input);
  if (!d) return fallback;
  const day = ordinal(d.getDate());
  const mon = MONTHS[d.getMonth()];
  const y = d.getFullYear();
  const now = new Date().getFullYear();
  return y === now ? `${day} ${mon}` : `${day} ${mon} ${y}`;
}

// "2nd Mar, 14:32" or "2nd Mar 2025, 14:32" (24-hour clock, always IST).
export function formatDateTime(input, { fallback = "—" } = {}) {
  const d = toDate(input);
  if (!d) return fallback;
  const { day, month, year, hh, mm } = istFields(d);
  const datePart = year === new Date().getFullYear()
    ? `${ordinal(day)} ${MONTHS[month - 1]}`
    : `${ordinal(day)} ${MONTHS[month - 1]} ${year}`;
  return `${datePart}, ${hh}:${mm}`;
}

// "14:32" — pure time only, always IST regardless of device timezone.
export function formatTime(input, { fallback = "—" } = {}) {
  const d = toDate(input);
  if (!d) return fallback;
  const { hh, mm } = istFields(d);
  return `${hh}:${mm}`;
}

export default formatDate;

// Replace any "YYYY-MM-DD" tokens inside a string with the human format.
// Useful for server-built strings like "2026-03-02 → 2026-03-05".
export function humaniseDatesIn(text) {
  if (!text || typeof text !== "string") return text;
  return text.replace(/\b\d{4}-\d{2}-\d{2}\b/g, (m) => formatDate(m));
}

// "YYYY-MM-DD" for *right now*, always the IST calendar day — regardless of
// device timezone. Use this (not `new Date().toISOString().slice(0,10)`,
// which is UTC and drifts a day late-night IST) as the default date for
// anything the studio logs "for today" — timesheets, quick notes, etc.
export function todayISOInIST() {
  const { day, month, year } = istFields(new Date());
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

